import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  Renderer2,
  Signal,
  signal,
  viewChildren,
  WritableSignal,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { QueryParams } from '../../../models/navigation/navigationParams.interface';
import { SidePanelService } from '../services/side-panel.service';
import { PanelLayer } from '../types/panel-layer';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter, fromEvent } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-side-panel',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  templateUrl: './side-panel.component.html',
  styleUrls: ['./side-panel.component.scss'],
  animations: [
    trigger('appear', [
      transition(':enter', [style({ left: '0px' }), animate('.3s ease-out', style({ left: '-1000px' }))]),
      transition(':leave', [style({ left: '-1000px' }), animate('.3s ease-out', style({ left: '0px' }))]),
    ]),
  ],
})
export class SidePanelComponent implements AfterViewInit {
  @Input() public appContent: HTMLDivElement;
  public layers: WritableSignal<PanelLayer[]> = signal([]);

  protected panelContents: Signal<readonly ElementRef<HTMLElement>[]> = viewChildren('panelContent');

  protected panelLayers: WritableSignal<(PanelLayer & { animationDisabled: boolean })[]> = signal([]);

  private readonly panelOwner: WritableSignal<HTMLElement | null> = signal(null);

  constructor(
    private readonly _service: SidePanelService,
    private readonly _renderer: Renderer2,
    private readonly _host: ElementRef,
    private readonly _destroyRef: DestroyRef
  ) {
    this._disableOrEnableAnimationsOnLayers();
  }

  public get isOpen() {
    return this.layers().length;
  }

  public ngAfterViewInit(): void {
    this._service.panel = this;
    this._closePanelsIfClickedOutside();
  }

  public openStacked(owner: HTMLElement, layers: PanelLayer[]) {
    this.panelOwner.set(owner);
    this.layers.set(layers);
  }

  public closeAllStackedPanels(): void {
    this.panelOwner.set(null);
    this.layers.set([]);
  }

  public isInside(target: HTMLElement) {
    return (this._host.nativeElement as HTMLElement).contains(target);
  }

  protected closePanel($event: MouseEvent, toClose: PanelLayer) {
    $event.stopPropagation();
    this._service.closePanel(Object.keys(toClose.dependsOn) as QueryParams[]);
  }

  private _disableOrEnableAnimationsOnLayers() {
    toObservable(this.layers)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((currentLayers) => {
        const previousPanels = this.panelLayers();
        const previousPanelContents = this.panelContents();

        const newLayers = currentLayers.map((layer, index) => {
          const panelHadSameContentComponent = previousPanels[index]?.component.name === layer.component.name;
          if (panelHadSameContentComponent) {
            // If animation on this layer previously was disabled, we want to enable it to animate closing
            // It can be done only manually with renderer, as Angular cannot update "@.disabled" for previous layer
            // and at the same time create new layers
            // Check comment for more info https://github.com/angular/components/issues/4227#issuecomment-348148642
            this._renderer.setProperty(previousPanelContents[index].nativeElement, '@.disabled', true);
          }
          return { ...layer, animationDisabled: panelHadSameContentComponent };
        });

        let allPanelsAreClosed = previousPanels.length > 0 && currentLayers.length === 0;
        if (allPanelsAreClosed) {
          previousPanelContents.forEach((content) =>
            this._renderer.setProperty(content.nativeElement, '@.disabled', false)
          );
        }

        this.panelLayers.set(newLayers);
      });
  }

  private _closePanelsIfClickedOutside() {
    // The side-panel registers click listeners on the app-content div to detect outside clicks.
    // We don't want to register the click handlers on the body/document to prevent interference with dialogs
    fromEvent<PointerEvent>(this.appContent, 'click')
      .pipe(
        filter(() => !!this.panelOwner()),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((event: PointerEvent) => {
        // If the click was inside the side-panel, or the host, do nothing, otherwise, close
        const ownerClicked = this.panelOwner()?.contains(event.target as Node);
        const hostClicked = this._host?.nativeElement?.contains(event.target);
        if (ownerClicked || hostClicked) return;

        this._service.close();
      });
  }
}
